import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject, model, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { PaymentMethodTypeStore } from '@digiwall/goliath-contacts';
import { BehaviorSubject, Observable, combineLatest, concatMap, map, of } from 'rxjs';
import {
  CreateUpdatePaymentLineDto,
  CustomerDeliveryNoteService,
  CustomerInvoiceService,
  CustomerReturnNoteService,
  DayBookService,
  DocPaymentLookupDto,
  DocPaymentNature,
} from '@digiwall/goliath-documents/proxy';
import { toObservable } from '@angular/core/rxjs-interop';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { PaymentMethodDto } from '@digiwall/goliath-contacts/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { ErrorFormControlDirective } from '@digiwall/core';
import { DwInputAutoCompleteDirective } from '@digiwall/core';
import { AsyncPipe, CurrencyPipe } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { DisplayNamePipe } from '@digiwall/core';
import { FileUrlPipe } from '@digiwall/file-management';
import { PaymentHistoryListComponent } from './payment-history/payment-history-list.component';

@Component({
  selector: 'document-customer-invoice-payments',
  templateUrl: './customer-invoice-payments.component.html',
  styleUrl: './customer-invoice-payments.scss',
  imports: [
    CommonModule,
    DialogBoxComponent,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    ErrorFormControlDirective,
    DwInputAutoCompleteDirective,
    AsyncPipe,
    CurrencyPipe,
    LocalizationModule,
    DisplayNamePipe,
    FileUrlPipe,
    PaymentHistoryListComponent,
  ],
})
export class CustomerInvoicePaymentsComponent implements OnInit {
  paymentMethodEnumerationTypeStore = inject(PaymentMethodTypeStore);

  @Input() invoiceId: string;
  service = signal<
    CustomerInvoiceService | CustomerDeliveryNoteService | CustomerReturnNoteService
  >(null);
  readonly = model<boolean>(false);

  PaymentNature = DocPaymentNature;
  payementHistory = [];
  payments$: BehaviorSubject<DocPaymentLookupDto[]> = new BehaviorSubject([]);

  paymentMethods$: Observable<PaymentMethodDto[]>;
  filteredPaymentMethods: PaymentMethodDto[] = [];

  originalRemainingAmount$ = new BehaviorSubject<number>(0);
  totalAmount$ = new BehaviorSubject<number>(0);
  remainingAmount$ = new BehaviorSubject<number>(0);

  form: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private dayBookService: DayBookService,
    private toasterService: ToasterService,
    private confirmationService: ConfirmationService,
  ) {
    this.initForm();

    this.paymentMethods$ = toObservable(this.paymentMethodEnumerationTypeStore.entities);
  }

  ngOnInit(): void {
    combineLatest([this.originalRemainingAmount$, this.totalAmount$]).subscribe(
      ([originalAmount, totalAmount]) => {
        const remainingAmount = parseFloat((originalAmount - totalAmount).toFixed(4));
        this.remainingAmount$.next(remainingAmount);
      },
    );

    combineLatest([this.dayBookService.getCurrentSalesSlipPaymentMethods(), this.paymentMethods$])
      .pipe(
        map(([currentPaymentMethodIds, paymentMethods]) => {
          return paymentMethods.filter(paymentMethod =>
            currentPaymentMethodIds.includes(paymentMethod.id),
          );
        }),
      )
      .subscribe(paymentMethods => {
        this.filteredPaymentMethods = paymentMethods;
        this.setPaymentMethodControls();
      });

    this.getRemainingAmount();
    this.getPayments();
  }

  initForm() {
    this.form = this.formBuilder.group({
      paymentMethods: this.formBuilder.array([]),
    });
  }

  get paymentMethods(): FormArray {
    return this.form.get('paymentMethods') as FormArray;
  }

  setPaymentMethodControls() {
    this.paymentMethods.clear();

    this.filteredPaymentMethods.forEach(paymentMethod => {
      let paymentMethodControl = this.formBuilder.control(paymentMethod.id, [Validators.required]);
      let amountControl = this.formBuilder.control(null);
      paymentMethodControl.disable();
      amountControl.disable();

      this.paymentMethods.controls.push(
        this.formBuilder.group({
          selected: [false],
          paymentMethodId: paymentMethodControl,
          amount: amountControl,
          logo: paymentMethod.logo,
        }),
      );
    });
  }

  selectPayment(paymentControl: AbstractControl, paymentInputRef: HTMLInputElement) {
    let selectedControl = paymentControl.get('selected');
    let amountControl = paymentControl.get('amount');

    if (selectedControl != null && amountControl != null) {
      selectedControl.patchValue(!selectedControl.value);

      selectedControl.value ? amountControl.enable() : amountControl.disable();

      amountControl.patchValue(selectedControl.value ? this.remainingAmount$.getValue() : null);

      if (selectedControl.value) {
        paymentInputRef.focus();
        paymentInputRef.select();
      }

      this.computeTotalAmount();

      this.form.markAsDirty();
    }
  }

  paymentAmountChanged(paymentControl: AbstractControl) {
    if (paymentControl.get('amount').value == null) {
      paymentControl.get('selected').patchValue(false);
      paymentControl.get('amount').disable();
    }
    this.computeTotalAmount();
  }

  computeTotalAmount() {
    let totalAmount = this.paymentMethods.controls.reduce(
      (total, currentPayementControl) => total + currentPayementControl.get('amount').value,
      0,
    );

    this.totalAmount$.next(Math.round(totalAmount * 1000) / 1000);
  }

  save() {
    if (this.readonly()) return;

    const payementLines = this.paymentMethods.controls
      .map(control => {
        return {
          paymentMethod: control.get('paymentMethodId')?.value,
          amount: control.get('amount')?.value,
        } as CreateUpdatePaymentLineDto;
      })
      .filter(payementLine => payementLine.paymentMethod != null && payementLine.amount);

    if (payementLines.length <= 0) {
      return;
    }

    this.service()
      .addPayment(this.invoiceId, payementLines)
      .subscribe(_ => {
        this.toasterService.success('::SavedSuccessfully');

        this.refreshPayments();
        this.form.markAsPristine();
      });
  }

  private getRemainingAmount() {
    this.service()
      .getRemainingAmount(this.invoiceId)
      .subscribe(remainingAmount => this.originalRemainingAmount$.next(remainingAmount));
  }

  private getPayments() {
    this.service()
      .getPayments(this.invoiceId)
      .subscribeOnce(payments => this.payments$.next(payments));
  }

  cancelPayment(payementId) {
    this.confirmationService
      .warn('Doc::CustomerInvoice:CancelPayment', '::AreYouSure')
      .pipe(
        concatMap(confirmationStatus => {
          if (confirmationStatus == Confirmation.Status.confirm) {
            return this.service()
              .reversePayment(this.invoiceId, payementId)
              .pipe(map(() => true));
          }
          return of(null);
        }),
      )
      .subscribe(paymentCancelled => {
        if (paymentCancelled) {
          this.toasterService.success('Doc::CustomerInvoice:PaymentReversed');
          this.refreshPayments();
        }
      });
  }

  private refreshPayments() {
    this.getRemainingAmount();
    this.getPayments();

    this.form.reset();

    this.setPaymentMethodControls();
    this.computeTotalAmount();
  }
}
